import { useState } from 'react';

import { Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';

import { Send } from 'domains/campaigns/types';
import Select, { Value } from 'domains/core/components/Select';
import {
    ChannelSegmentFilter,
    RecordTypeFilter,
    SegmentFilter,
    SegmentFilterCategorySpecificField,
    SegmentFilterEngagementFields,
    SegmentFilterEventFields,
    SegmentFilterState,
} from 'domains/segments/types';
import { FilterCategory } from 'models/enums';

import ActivityFilter from './ActivityFilter';
import DeleteFilter from './DeleteFilter';
import DemographicFilter from './DemographicFilter';
import EventFilter from './EventFilter';
import IncludeNullValuesCheckbox from './IncludeNullValuesCheckbox';

const matchTypes = ['AND', 'OR'];

export type Props = {
    eventRecordTypeFilters: RecordTypeFilter[];
    objectRecordTypeFilters: RecordTypeFilter[];
    demographicFilters: SegmentFilter[];
    engagementFilters: SegmentFilter[] | ChannelSegmentFilter[];
    filter: SegmentFilterState<SegmentFilterCategorySpecificField>;
    hasContactObjectFlag: boolean;
    hideMatchType: boolean;
    index: number;
    sends: Send[];
    value: any;
    handleChange: (e: any) => void;
    handleDatetimeError: (filterId: string, error: boolean) => void;
    removeFilter: (index: number) => void;
    updateFilter: (
        index: number,
        filter: SegmentFilterState<SegmentFilterCategorySpecificField>,
        resetPreview?: boolean
    ) => void;
};

const Filter = ({
    eventRecordTypeFilters,
    objectRecordTypeFilters,
    demographicFilters,
    engagementFilters,
    filter,
    hasContactObjectFlag,
    hideMatchType,
    index,
    sends,
    value,
    handleChange,
    handleDatetimeError,
    removeFilter,
    updateFilter,
}: Props) => {
    const filterCategories: Value[] = Object.values(FilterCategory)
        .filter((category) => hasContactObjectFlag || category !== FilterCategory.CONTACT_OBJECTS)
        .map((category) => ({ label: category, value: category }));
    const isCategoryActivity = Boolean(filter.category === FilterCategory.CONTACT_ACTIVITY);
    const isCategoryDetails = Boolean(filter.category === FilterCategory.CONTACT_DETAILS);
    const isCategoryEvent = Boolean(filter.category === FilterCategory.CONTACT_EVENTS);
    const isCategoryObjects = Boolean(filter.category === FilterCategory.CONTACT_OBJECTS);
    const isEventOrObjectCategory = isCategoryEvent || isCategoryObjects;

    const [isFilterHighlighted, setIsFilterHighlighted] = useState(false);
    const [isOperatorValueHighlighted, setIsOperatorValueHighlighted] = useState(false);

    const updateSegmentFilter = (
        index: number,
        filter: SegmentFilterState<SegmentFilterCategorySpecificField>,
        resetPreview?: boolean
    ) => updateFilter(index, filter, resetPreview);

    return (
        <Stack>
            <Stack direction="row" flexWrap="wrap">
                <Stack
                    columnGap={2}
                    direction="row"
                    flexWrap="wrap"
                    key={filter.id}
                    rowGap={2}
                    sx={{
                        backgroundColor: isFilterHighlighted ? 'action.hover' : 'transparent',
                        border: '1px solid',
                        borderColor: 'other.divider',
                        borderRadius: '6px',
                        padding: '32px 22px',
                    }}
                >
                    <Stack order="1" rowGap={1.5}>
                        <Select
                            data-test="category-select"
                            data-testid="category-select"
                            label="Filter type"
                            sx={{ textTransform: 'capitalize', width: '12rem' }}
                            value={filter.category ?? ''}
                            values={filterCategories}
                            onChange={(e: any) => {
                                updateSegmentFilter(index, {
                                    ...filter,
                                    category: e.target.value,
                                    categorySpecificFields: {
                                        value: '',
                                    },
                                    name: '',
                                    operator: null,
                                    autocompleteValue: null,
                                    autocompleteInputValue: '',
                                });
                            }}
                        />
                        {isCategoryDetails && (
                            <IncludeNullValuesCheckbox
                                isNullValuesIncluded={filter.includeNullValues}
                                updateFilter={() => {
                                    updateSegmentFilter(index, {
                                        ...filter,
                                        includeNullValues: !filter.includeNullValues,
                                    });
                                }}
                            />
                        )}
                    </Stack>
                    {isCategoryActivity && (
                        <ActivityFilter
                            filter={filter as SegmentFilterState<SegmentFilterEngagementFields>}
                            index={index}
                            engagementFilters={engagementFilters}
                            sends={sends}
                            updateSegmentFilter={updateSegmentFilter}
                        />
                    )}
                    {isCategoryDetails && (
                        <DemographicFilter
                            filter={filter}
                            filterOptions={demographicFilters}
                            index={index}
                            handleDatetimeError={handleDatetimeError}
                            updateSegmentFilter={updateSegmentFilter}
                        />
                    )}
                    {isEventOrObjectCategory && (
                        <EventFilter
                            eventRecordTypeFilters={isCategoryEvent ? eventRecordTypeFilters : objectRecordTypeFilters}
                            filter={filter as SegmentFilterState<SegmentFilterEventFields>}
                            index={index}
                            isOperatorValueHighlighted={isOperatorValueHighlighted}
                            handleDatetimeError={handleDatetimeError}
                            updateSegmentFilter={updateSegmentFilter}
                        />
                    )}
                    <DeleteFilter
                        filter={filter}
                        index={index}
                        isCategoryEvent={isEventOrObjectCategory}
                        removeFilter={removeFilter}
                        setIsFilterHighlighted={setIsFilterHighlighted}
                        setIsOperatorValueHighlighted={setIsOperatorValueHighlighted}
                        updateSegmentFilter={updateSegmentFilter}
                    />
                </Stack>
            </Stack>
            {!hideMatchType && (
                <ToggleButtonGroup
                    aria-label="match type"
                    exclusive
                    onChange={handleChange}
                    style={{ margin: '16px' }}
                    value={value}
                >
                    {matchTypes.map((type) => (
                        <ToggleButton
                            aria-label={type}
                            key={type}
                            name="matchType"
                            sx={{ textTransform: 'lowercase' }}
                            value={type}
                        >
                            {type}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            )}
        </Stack>
    );
};

export default Filter;
